import { useRef, useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { stressStore, crisisLevel } from "../store";

const dummy = new THREE.Object3D();

// glowing data packets streaming from the base up into the Publisher Vault
function PaperStream({ count = 90 }) {
  const ref = useRef();
  const packets = useMemo(
    () =>
      new Array(count).fill(0).map(() => ({
        angle: Math.random() * Math.PI * 2,
        radius: 0.6 + Math.random() * 1.4,
        phase: Math.random(),
        speed: 0.15 + Math.random() * 0.25,
        spin: Math.random() * Math.PI,
      })),
    [count]
  );

  useFrame((s, dt) => {
    const crisis = crisisLevel(stressStore.getState());
    const t = s.clock.elapsedTime;
    const mul = 0.6 + crisis * 2.4; // more output extracted under pressure
    for (let i = 0; i < count; i++) {
      const p = packets[i];
      p.phase = (p.phase + dt * p.speed * mul) % 1;
      const y = 0.5 + p.phase * 4.5; // base -> vault
      const conv = 1 - p.phase * 0.75; // spiral inward as it rises
      const a = p.angle + t * 0.4;
      dummy.position.set(Math.cos(a) * p.radius * conv, y, Math.sin(a) * p.radius * conv);
      dummy.rotation.set(t + p.spin, t * 0.5 + p.spin, 0);
      const sc = 0.05 + Math.sin(p.phase * Math.PI) * 0.02;
      dummy.scale.set(sc, sc * 1.3, 0.01);
      dummy.updateMatrix();
      ref.current.setMatrixAt(i, dummy.matrix);
    }
    ref.current.instanceMatrix.needsUpdate = true;
    ref.current.material.emissiveIntensity = 1.6 + crisis * 1.5;
  });

  return (
    <instancedMesh ref={ref} args={[null, null, count]}>
      <boxGeometry args={[1, 1, 1]} />
      <meshStandardMaterial
        color="#bfe3ff"
        emissive="#7cc4ff"
        emissiveIntensity={1.6}
        roughness={0.2}
        metalness={0.1}
        transparent
        opacity={0.95}
      />
    </instancedMesh>
  );
}

export default function PublisherVault() {
  const hub = useRef();
  const ring1 = useRef();
  const ring2 = useRef();
  const core = useRef();

  useFrame((s) => {
    const t = s.clock.elapsedTime;
    const crisis = crisisLevel(stressStore.getState());
    if (hub.current) {
      hub.current.position.y = 5.1 + Math.sin(t * 0.6) * 0.12;
      hub.current.rotation.y = t * 0.25;
    }
    if (ring1.current) ring1.current.rotation.z = t * 0.6;
    if (ring2.current) ring2.current.rotation.x = -t * 0.9;
    if (core.current) {
      const p = 1.3 + Math.sin(t * 2) * 0.4 + crisis * 1.5;
      core.current.material.emissiveIntensity = p;
    }
  });

  return (
    <group>
      <PaperStream />
      <group ref={hub} position={[0, 5.1, 0]}>
        {/* glowing core */}
        <mesh ref={core}>
          <icosahedronGeometry args={[0.32, 1]} />
          <meshStandardMaterial
            color="#0a2a4a"
            emissive="#38bdf8"
            emissiveIntensity={1.4}
            metalness={0.6}
            roughness={0.2}
          />
        </mesh>
        {/* orbiting tech rings */}
        <mesh ref={ring1}>
          <torusGeometry args={[0.62, 0.02, 10, 60]} />
          <meshStandardMaterial color="#9fe0ff" emissive="#38bdf8" emissiveIntensity={1.2} metalness={0.8} roughness={0.2} />
        </mesh>
        <mesh ref={ring2}>
          <torusGeometry args={[0.82, 0.015, 10, 60]} />
          <meshStandardMaterial color="#d6f0ff" emissive="#7cc4ff" emissiveIntensity={0.9} metalness={0.8} roughness={0.2} />
        </mesh>
        {/* thin support struts / halo */}
        <mesh rotation={[Math.PI / 2, 0, 0]}>
          <ringGeometry args={[0.95, 1.0, 48]} />
          <meshBasicMaterial color="#7cc4ff" transparent opacity={0.25} side={THREE.DoubleSide} />
        </mesh>
      </group>
    </group>
  );
}
